/**
 * ArchiveCondominiumDialog Component
 * Handles condominium archiving with handover report
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { Archive, CheckCircle2, AlertTriangle, Download, User, Euro, FileCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_NAMES = {
  ACCOUNTING: 'Contabilità',
  LEGAL: 'Documentazione Legale',
  TECHNICAL: 'Documentazione Tecnica',
  CONTRACTS: 'Contratti',
  OTHER: 'Altro'
};

const CATEGORY_COLORS = {
  ACCOUNTING: 'bg-blue-100 text-blue-800',
  LEGAL: 'bg-purple-100 text-purple-800',
  TECHNICAL: 'bg-green-100 text-green-800',
  CONTRACTS: 'bg-orange-100 text-orange-800',
  OTHER: 'bg-gray-100 text-gray-800'
};

export default function ArchiveCondominiumDialog({ 
  open, 
  onOpenChange, 
  condominiumId, 
  condominiumName,
  onArchived 
}) {
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1: Info, 2: Checklist, 3: Review, 4: Archive
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [checklist, setChecklist] = useState([]);
  const [categories, setCategories] = useState({});
  
  const [formData, setFormData] = useState({
    incoming_admin_name: '',
    incoming_admin_email: '',
    effective_date: new Date().toISOString().split('T')[0],
    notes: '',
    pending_issues: '',
    reason: 'Cessazione incarico amministrativo'
  });

  // Load or create handover report
  const initializeReport = useCallback(async () => {
    if (!condominiumId || !token) return;
    
    setLoading(true);
    try {
      // Create or get existing report
      const createRes = await fetch(`${API_URL}/api/condominiums/${condominiumId}/handover/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (createRes.ok) {
        const createData = await createRes.json();
        
        // Fetch full report
        const reportRes = await fetch(
          `${API_URL}/api/condominiums/${condominiumId}/handover/${createData.id}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (reportRes.ok) {
          const reportData = await reportRes.json();
          setReport(reportData);
          setChecklist(reportData.checklist || []);
          
          // Group by category
          const cats = {};
          for (const item of reportData.checklist || []) {
            const cat = item.category;
            if (!cats[cat]) cats[cat] = [];
            cats[cat].push(item);
          }
          setCategories(cats);
        }
      }
    } catch (error) {
      console.error('Error initializing report:', error);
      toast.error('Errore nel caricamento del verbale');
    } finally {
      setLoading(false);
    }
  }, [condominiumId, token]);

  useEffect(() => {
    if (open) {
      initializeReport();
      setStep(1);
    }
  }, [open, initializeReport]);

  const handleChecklistChange = (itemId, field, value) => {
    setChecklist(prev => prev.map(item => 
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const saveChecklist = async () => {
    if (!report?.id) return;
    
    try {
      await fetch(`${API_URL}/api/condominiums/${condominiumId}/handover/${report.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          checklist,
          incoming_admin_name: formData.incoming_admin_name,
          incoming_admin_email: formData.incoming_admin_email,
          effective_date: formData.effective_date,
          notes: formData.notes,
          pending_issues: formData.pending_issues
        })
      });
    } catch (error) {
      console.error('Error saving checklist:', error);
    }
  };

  const signReport = async () => {
    if (!report?.id) return false;
    
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condominiumId}/handover/${report.id}/sign`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      return res.ok;
    } catch (error) {
      console.error('Error signing report:', error);
      return false;
    }
  };

  const downloadPdf = async () => {
    if (!report?.id) return;
    
    try {
      const res = await fetch(
        `${API_URL}/api/condominiums/${condominiumId}/handover/${report.id}/pdf`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `verbale_consegne_${condominiumName.replace(/\s/g, '_')}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      toast.error('Errore nel download del PDF');
    }
  };

  const handleArchive = async () => {
    setLoading(true);
    
    try {
      // Save checklist first
      await saveChecklist();
      
      // Sign the report
      const signed = await signReport();
      if (!signed) {
        toast.error('Errore nella firma del verbale');
        setLoading(false);
        return;
      }
      
      // Archive the condominium
      const res = await fetch(`${API_URL}/api/condominiums/${condominiumId}/archive`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          handover_report_id: report.id,
          reason: formData.reason
        })
      });
      
      if (res.ok) {
        toast.success(`Condominio "${condominiumName}" archiviato con successo`);
        onOpenChange(false);
        if (onArchived) onArchived();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore nell\'archiviazione');
      }
    } catch (error) {
      toast.error('Errore nell\'archiviazione');
    } finally {
      setLoading(false);
    }
  };

  const checkedCount = checklist.filter(item => item.is_checked).length;
  const progress = checklist.length > 0 ? (checkedCount / checklist.length) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Archive className="h-5 w-5 text-orange-600" />
            Archivia Condominio
          </DialogTitle>
          <DialogDescription>
            {condominiumName} - Verbale di passaggio consegne
          </DialogDescription>
        </DialogHeader>

        {loading && !report ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <Tabs value={`step-${step}`} className="w-full">
            {/* Progress indicator */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-sm mb-2">
                <span>Progresso: {checkedCount}/{checklist.length} documenti verificati</span>
                <span>{Math.round(progress)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="step-1" onClick={() => setStep(1)}>
                1. Informazioni
              </TabsTrigger>
              <TabsTrigger value="step-2" onClick={() => setStep(2)}>
                2. Checklist
              </TabsTrigger>
              <TabsTrigger value="step-3" onClick={() => setStep(3)}>
                3. Riepilogo
              </TabsTrigger>
              <TabsTrigger value="step-4" onClick={() => setStep(4)}>
                4. Archivia
              </TabsTrigger>
            </TabsList>

            {/* Step 1: Information */}
            <TabsContent value="step-1" className="space-y-4 mt-4">
              {/* Financial Summary */}
              {report && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Euro className="h-4 w-4" />
                      Situazione Finanziaria Attuale
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-700">
                          €{(report.bank_balance || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-green-600">Saldo Bancario</div>
                      </div>
                      <div className="text-center p-3 bg-red-50 rounded-lg">
                        <div className="text-2xl font-bold text-red-700">
                          €{(report.delinquency_total || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-red-600">Morosità Totale</div>
                      </div>
                      <div className="text-center p-3 bg-orange-50 rounded-lg">
                        <div className="text-2xl font-bold text-orange-700">
                          €{(report.pending_payables || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                        </div>
                        <div className="text-xs text-orange-600">Debiti Fornitori</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Incoming Admin */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Amministratore Entrante (opzionale)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nome e Cognome</Label>
                      <Input
                        value={formData.incoming_admin_name}
                        onChange={(e) => setFormData({ ...formData, incoming_admin_name: e.target.value })}
                        placeholder="es. Mario Rossi"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={formData.incoming_admin_email}
                        onChange={(e) => setFormData({ ...formData, incoming_admin_email: e.target.value })}
                        placeholder="es. nuovo.admin@email.it"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Data Effettiva Passaggio</Label>
                    <Input
                      type="date"
                      value={formData.effective_date}
                      onChange={(e) => setFormData({ ...formData, effective_date: e.target.value })}
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button onClick={() => setStep(2)}>
                  Continua alla Checklist →
                </Button>
              </div>
            </TabsContent>

            {/* Step 2: Checklist */}
            <TabsContent value="step-2" className="space-y-4 mt-4">
              {Object.entries(categories).map(([category, items]) => (
                <Card key={category}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Badge className={CATEGORY_COLORS[category]}>
                        {CATEGORY_NAMES[category]}
                      </Badge>
                      <span className="text-muted-foreground">
                        ({items.filter(i => checklist.find(c => c.id === i.id)?.is_checked).length}/{items.length})
                      </span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {items.map((item) => {
                      const checkItem = checklist.find(c => c.id === item.id) || item;
                      return (
                        <div key={item.id} className="flex items-start gap-3 p-2 rounded hover:bg-slate-50">
                          <Checkbox
                            checked={checkItem.is_checked}
                            onCheckedChange={(checked) => handleChecklistChange(item.id, 'is_checked', checked)}
                          />
                          <div className="flex-1">
                            <div className="text-sm">{item.description}</div>
                            <Input
                              className="mt-1 h-7 text-xs"
                              placeholder="Note (opzionale)"
                              value={checkItem.notes || ''}
                              onChange={(e) => handleChecklistChange(item.id, 'notes', e.target.value)}
                            />
                          </div>
                          {checkItem.is_checked && (
                            <CheckCircle2 className="h-4 w-4 text-green-500 mt-1" />
                          )}
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              ))}

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)}>
                  ← Indietro
                </Button>
                <Button onClick={() => { saveChecklist(); setStep(3); }}>
                  Continua al Riepilogo →
                </Button>
              </div>
            </TabsContent>

            {/* Step 3: Review */}
            <TabsContent value="step-3" className="space-y-4 mt-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Note e Questioni in Sospeso</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Note Generali</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Eventuali note sul passaggio di consegne..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Questioni in Sospeso</Label>
                    <Textarea
                      value={formData.pending_issues}
                      onChange={(e) => setFormData({ ...formData, pending_issues: e.target.value })}
                      placeholder="Cause legali, lavori in corso, delibere da eseguire..."
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Summary */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileCheck className="h-4 w-4" />
                    Riepilogo Checklist
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    {Object.entries(categories).map(([category, items]) => {
                      const checked = items.filter(i => checklist.find(c => c.id === i.id)?.is_checked).length;
                      const total = items.length;
                      const complete = checked === total;
                      
                      return (
                        <div key={category} className={`p-3 rounded-lg text-center ${complete ? 'bg-green-50' : 'bg-amber-50'}`}>
                          <div className={`text-lg font-bold ${complete ? 'text-green-700' : 'text-amber-700'}`}>
                            {checked}/{total}
                          </div>
                          <div className="text-xs text-muted-foreground">{CATEGORY_NAMES[category]}</div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)}>
                  ← Indietro
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={downloadPdf}>
                    <Download className="h-4 w-4 mr-2" />
                    Scarica PDF
                  </Button>
                  <Button onClick={() => { saveChecklist(); setStep(4); }}>
                    Procedi all'Archiviazione →
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Step 4: Archive */}
            <TabsContent value="step-4" className="space-y-4 mt-4">
              <Card className="border-orange-200 bg-orange-50">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2 text-orange-800">
                    <AlertTriangle className="h-5 w-5" />
                    Conferma Archiviazione
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-orange-700">
                    Stai per archiviare il condominio <strong>"{condominiumName}"</strong>. 
                    Questa azione:
                  </p>
                  <ul className="text-sm text-orange-700 list-disc list-inside space-y-1">
                    <li>Firmerà digitalmente il verbale di passaggio consegne</li>
                    <li>Imposterà il condominio come "Archiviato"</li>
                    <li>I dati resteranno consultabili ma non modificabili</li>
                    <li>Sarà possibile ripristinare il condominio in futuro</li>
                  </ul>
                  
                  <div>
                    <Label>Motivo dell'archiviazione</Label>
                    <Input
                      value={formData.reason}
                      onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                      placeholder="es. Cessazione incarico, Revoca, etc."
                    />
                  </div>
                </CardContent>
              </Card>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(3)}>
                  ← Indietro
                </Button>
                <Button 
                  onClick={handleArchive}
                  disabled={loading}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Archiviazione in corso...
                    </>
                  ) : (
                    <>
                      <Archive className="h-4 w-4 mr-2" />
                      Archivia Condominio
                    </>
                  )}
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}
